<template>
	<div class="page">
        <div class="flex flex-col w-full h-52 items-center justify-center splash-screen-loading-box" v-if="loading">
            <ProgressSpinner />
        </div>
        <span v-else>
            <div class="card-css page-header rounded-md py-4 md:py-6 px-3 mb-4">
                <div class="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center w-full">
                    <h1 class="text-xl md:text-2xl font-semibold">{{$t('activities.activity')}}</h1>
                    <div class="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                        <Button 
                            v-if="$route.params.id" 
                            @click="goDashboard" 
                            severity="info" 
                            outlined
                            :label="$t('activities.dashboard')"
                            class="w-full sm:w-auto"
                        > 
                            <template #icon>
                                <Icon :size="20" :name="LinkIcon"></Icon>
                            </template>
                        </Button>
                        <Button 
                            v-if="$route.params.id" 
                            @click="showModalRef = true" 
                            severity="danger" 
                            outlined
                            :label="$t('general.delete')"
                            class="w-full sm:w-auto"
                        >
                            <template #icon>
                                <Icon :size="20" :name="TrashIcon"></Icon>
                            </template>
                        </Button>
                        <Button 
                            @click="validateForms" 
                            :loading="loadingBtnSave" 
                            :label="$t('general.save')"
                            class="w-full sm:w-auto"
                        >
                            <template #icon>
                                <Icon :size="20" :name="SaveIcon"></Icon>
                            </template>
                        </Button>
                    </div>
                </div>
            </div>
            <form v-if="current === 1" class="space-y-4 md:space-y-6 mb-5">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                    <!-- Name Activity -->
                    <div class="flex flex-col gap-2 md:col-span-1 ">
                        <label class="text-sm font-medium">{{$t('activities.name_activity')}} <span class="text-red-500">*</span></label>
                        <InputText 
                            v-model="event.name" 
                            :placeholder="$t('activities.tl_activity', { company: organizationName }) + '...'"
                            :class="{'p-invalid': errors.name}"
                            class="w-full"
                        />
                        <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                    </div>

                    <!-- Date Hour -->
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium">{{$t('activities.date_hour')}} <span class="text-red-500">*</span></label>
                        <Calendar 
                            v-model="event.date" 
                            showTime 
                            hourFormat="24"
                            :class="{'p-invalid': errors.date}"
                            class="w-full"
                        />
                        <small v-if="errors.date" class="p-error">{{ errors.date }}</small>
                    </div>

                    <!-- Activity -->
                    <div class="flex flex-col gap-2 md:col-span-1 ">
                        <label class="text-sm font-medium">{{$t('activities.activity')}} <span class="text-red-500">*</span></label>
                        <Dropdown 
                            v-model="event.activity"
                            :options="activityOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('activities.select_activity')"
                            :disabled="$route.params.api_key !== undefined"
                            :class="{'p-invalid': errors.activity}"
                            class="w-full"
                        />
                        <small v-if="errors.activity" class="p-error">{{ errors.activity }}</small>
                    </div>

                    <!-- Language -->
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium">{{$t('activities.language')}} <span class="text-red-500">*</span></label>
                        <Dropdown 
                            v-model="event.language"
                            :options="languageOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('activities.select_language')"
                            :class="{'p-invalid': errors.language}"
                            class="w-full"
                        />
                        <small v-if="errors.language" class="p-error">{{ errors.language }}</small>
                    </div>
                    <!-- Demo Checkbox -->
                    <div class="flex flex-col gap-2 md:col-span-1">
                        <label for="demo" class="text-sm font-medium cursor-pointer">{{$t('activities.demo')}}</label>
                        <div class="flex items-center gap-2 pt-2">
                            <!-- PrimeVue tooltip doesn't trigger on disabled inputs -->
                            <span
                                class="inline-flex"
                                :tabindex="isEditingEvent ? 0 : undefined"
                                v-tooltip.top="isEditingEvent ? t('activities.demo_locked_tooltip') : undefined"
                            >
                                <Checkbox 
                                    v-model="event.demo" 
                                    inputId="demo"
                                    :binary="true"
                                    :disabled="isEditingEvent"
                                />
                            </span>
                        </div>
                    </div>

                    <div v-if="canUseJoinLobby" class="flex flex-col gap-3 md:col-span-2 lg:col-span-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <div class="flex items-center gap-2">
                            <span
                                class="inline-flex"
                                :tabindex="event.demo ? 0 : undefined"
                                v-tooltip.top="event.demo ? t('join_lobby.demo_disabled') : undefined"
                            >
                                <Checkbox
                                    v-model="event.join_lobby_enabled"
                                    inputId="join-lobby-enabled"
                                    :binary="true"
                                    :disabled="event.demo"
                                />
                            </span>
                            <label for="join-lobby-enabled" class="text-sm font-medium cursor-pointer">
                                {{ $t('join_lobby.enable') }}
                            </label>
                        </div>
                        <div v-if="event.join_lobby_enabled" class="flex flex-col gap-3">
                            <div class="flex items-center gap-2">
                                <Checkbox
                                    v-model="event.join_lobby_locations_enabled"
                                    inputId="join-lobby-locations-enabled"
                                    :binary="true"
                                />
                                <label for="join-lobby-locations-enabled" class="text-sm font-medium cursor-pointer">
                                    {{ $t('join_lobby.use_locations') }}
                                </label>
                            </div>
                            <div v-if="event.join_lobby_locations_enabled" class="flex flex-col gap-2">
                                <label class="text-sm font-medium">{{ $t('join_lobby.locations') }}</label>
                                <InputChips
                                    v-model="event.join_lobby_locations"
                                    class="w-full"
                                    :placeholder="$t('join_lobby.locations_placeholder')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Comments -->
                    <div class="flex flex-col gap-2 md:col-span-1 lg:col-span-2">
                        <label class="text-sm font-medium">{{$t('activities.comments')}}</label>
                        <Textarea 
                            v-model="event.comments"
                            :placeholder="$t('activities.comments') + '...'"
                            rows="4"
                            class="w-full"
                        />
                    </div>

                    <!-- Logo -->
                    <div class="flex flex-col gap-2 md:col-span-1 lg:col-span-2">
                        <label class="text-sm font-medium">{{$t('activities.logo')}}</label>
                        <div class="flex-col gap-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
                            <FileUpload 
                                mode="basic"
                                :fileLimit="1"
                                accept="image/*"
                                :maxFileSize="5000000"
                                @select="handleFileChange"
                                :auto="false"
                                :chooseLabel="$t('activities.click_add')"
                                class="w-full"
                            />
                            <div v-if="logo.length > 0" class="flex flex-col gap-2 mt-2 mx-auto">
                                <img 
                                    v-for="(file, index) in logo" 
                                    :key="index"
                                    :src="file.url" 
                                    :alt="file.name"
                                    class="w-32 h-32 object-cover rounded border"
                                />
                            </div>
                        </div>
                    </div>

                    
                </div>
            </form>

            <Dialog 
                v-model:visible="showModalRef"
                :modal="true"
                :closable="true"
                :style="{ width: '90vw', maxWidth: '500px' }"
            >
                <template #header>
                    <h3 class="text-lg font-semibold">{{$t('activities.delete')}}: {{ event.name }}</h3>
                </template>
                <p>{{$t('activities.sure')}}</p>
                <template #footer>
                    <Button 
                        label="Cancelar" 
                        severity="secondary" 
                        outlined 
                        @click="onNegativeClick"
                        class="mr-2"
                    />
                    <Button 
                        label="Aceptar" 
                        severity="danger"
                        @click="deleteEvent"
                    />
                </template>
            </Dialog>
        </span>
        <ActivityConfiguration />
	</div>
</template>

<script lang="ts" setup>
import Icon from "@/components/common/Icon.vue"
import Button from "primevue/button"
import InputText from "primevue/inputtext"
import Textarea from "primevue/textarea"
import Calendar from "primevue/calendar"
import Dropdown from "primevue/dropdown"
import FileUpload from "primevue/fileupload"
import Checkbox from "primevue/checkbox"
import InputChips from "primevue/inputchips"
import Dialog from "primevue/dialog"
import ProgressSpinner from "primevue/progressspinner"
import { useToast } from "primevue/usetoast"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useEventsStore } from "@/stores/apps/events"
import { axiosToken } from "@/utils/http";
import type { AxiosResponse } from "axios";
import { useContentStore } from "@/stores/apps/content"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { useOrganizationBrand } from "@/composables/useOrganizationBrand"
import ActivityConfiguration from "./ActivityConfiguration.vue"
import { cleanStore } from "@/utils/general"
import dayjs from "@/utils/dayjs"
import { getAvailableLanguages, getDefaultLanguage, getLanguagesText } from "@/lang/config"

const showModalRef = ref(false)
const TrashIcon = "carbon:trash-can"
const SaveIcon = "carbon:save"
const LinkIcon = "carbon:launch"
const storeEvents = useEventsStore()
const route = useRoute()
const router = useRouter()
const activities = computed(() => { return storeContent.activities})
const activityOptions = ref<Array<{label: string, value: string}>>([]);
const { t } = useStoreI18n()
const { organizationName } = useOrganizationBrand()
const languageOptions = computed(() => {
	const availableLanguages = getAvailableLanguages()
	const languagesText = getLanguagesText()
	
	return availableLanguages.map(lang => ({
		label: languagesText[lang] || lang,
		value: lang
	}))
});
const logo = ref<any>([])
const loading = ref(true)
const storeContent = useContentStore()
const booking = ref<any>(null)
const loadingBtnSave = ref(false)
const toast = useToast()
const errors = ref<Record<string, string>>({})
const isEditingEvent = computed(() => route.params.api_key != undefined)
const selectedActivityCode = computed(() => {
    if (!event.value.activity) return null
    const foundActivity = (storeContent.activities as any[]).find((activity: any) => activity.id == parseInt(event.value.activity || "0"))
    return foundActivity?.code || storeEvents.eventSelected.activity?.code || null
})
const canUseJoinLobby = computed(
    () => selectedActivityCode.value === "CAI" || selectedActivityCode.value === "MB" || selectedActivityCode.value === "CS"
)

onMounted( async () => {
    await cleanStore()
    await storeContent.getActivities()
    let logoLoaded: File | null = null
    if(route.params.api_key != undefined)
    {
        await storeEvents.getEvent(String(route.params.api_key))
        var eventData = storeEvents.eventSelected;
        console.log(eventData.logo);
        if(eventData.logo != undefined)
        {
            logoLoaded = await fetchLogo(eventData.logo)   
        }
        console.log({...eventData});
        
        const eventDate = eventData.date ? new Date(eventData.date) : new Date()
        const activityId = (eventData.activity as any)?.id?.toString() || null
        
        event.value = {
            id: eventData.id, 
            name: eventData.name, 
            date: eventDate, 
            comments: eventData.comments, 
            challenges: eventData.challenges, 
            activity: activityId, 
            logo: logoLoaded, 
            language: eventData.language, 
            demo: eventData.demo,
            join_lobby_enabled: eventData.join_lobby_enabled || false,
            join_lobby_locations_enabled: eventData.join_lobby_locations_enabled || false,
            join_lobby_locations: eventData.join_lobby_locations || [],
            join_lobby_status: eventData.join_lobby_status || "open",
        }
    }
    
    if('booking' in route.query && route.query.booking)
    {
        const bookingResult = await storeContent.fetchGatewayFind('booking', String(route.query.booking), 'id');
        booking.value = bookingResult[0]

        if(booking.value) {
            event.value.name = booking.value.name || ""
            
            if(booking.value.time)
            {
                event.value.date = dayjs(booking.value.date + ' ' + booking.value.time).toDate();
            }
            else if (booking.value.date)
            {
                event.value.date = dayjs(`${booking.value.date} 00:00:00`).toDate();
            }
            else
            {
                event.value.date = new Date();
            }

            event.value.language = booking.value.language || getDefaultLanguage()
            
            if(booking.value.logo_client)
            {
                event.value.logo = await fetchLogo(booking.value.logo_client)
            }
        }
    }

    activityOptions.value = (activities.value as any[]).map((v: any) => ({label: v.name, value: v.id.toString()}))
    if(route.query.demo && route.params.api_key == undefined )
    {
        event.value.demo = true
    }
    loading.value = false
    storeEvents.registerEventLogoBridge(setEventLogoPreview, eventHasLogo)
});

onBeforeUnmount(() => {
    storeEvents.clearEventLogoBridge()
})

const onNegativeClick = () =>
{
    toast.add({ severity: 'info', summary: t('general.canceled'), life: 3000 })
    showModalRef.value = false
}

const fetchLogo = async (logoUrl:string | URL | Request): Promise<File> =>
{
    let logoLoaded: File | null = null

    await fetch(logoUrl)
            .then((res) => res.blob())
            .then((myBlob) => {
                const filenameWithQueryParams = logoUrl.toString().split('/').pop(); // Get the last segment of the URL
                const filename = filenameWithQueryParams?.split('?')[0] || 'logo';
                logoLoaded = new File([myBlob], filename, {type: myBlob.type});
                logo.value.push({id: logoLoaded.name, name: logoLoaded.name, url: logoUrl.toString(), status: 'finished'})
            });
            
    return logoLoaded || new File([], 'logo')
}

const handleFileChange = (fileEvent: any) => {
    const file = fileEvent.files?.[0];
    if (file) {
        setEventLogoPreview(file);
    }
}

function setEventLogoPreview(file: File) {
    event.value.logo = file;
    const reader = new FileReader();
    reader.onload = (e) => {
        logo.value = [{
            id: file.name,
            name: file.name,
            url: e.target?.result as string,
            status: 'finished'
        }];
    };
    reader.readAsDataURL(file);
}

function eventHasLogo() {
    return Boolean(event.value.logo) || logo.value.length > 0
}

const deleteEvent = async () =>
{
    await axiosToken("delete", "events/event/" + route.params.api_key, {})
    .then( async (response: AxiosResponse) => {
        if(response.status == 202)
        {
            storeEvents.clearEventSelected()
            toast.add({ severity: 'success', summary: t('activities.delete_ok'), life: 3000 });
            await storeEvents.getEvents()
            router.push("/events_list")
        }
        else
        {
            toast.add({ severity: 'error', summary: t('activities.delete_error'), life: 3000 })
        }
    });
    showModalRef.value = false
} 

const saveEvent = async () => {
    loadingBtnSave.value = true
    var language = event.value.language;
    var type_request = route.params.api_key != undefined ? "put" : "post"
    var api_key = route.params.api_key != undefined ? route.params.api_key : "";
    var message_request = type_request == "put" ? " " + t('general.updated') + " " : " " + t('general.created') + " "
    var message_request_negative = type_request == "put" ? " " + t('general.update') + " " : " " + t('general.create') + " "
    var formData = new FormData();

    const date = event.value.date instanceof Date ? event.value.date : (event.value.date ? new Date(event.value.date) : new Date());
    
    formData.append("name", event.value.name || "");
    formData.append("date", date.toISOString());
    formData.append("activity", parseInt(event.value.activity || "0").toString());
    formData.append("comments", event.value.comments || "");
    formData.append("language", language);
    
    
    if (event.value.logo instanceof File) {
        formData.append("logo", event.value.logo);
    } else {
        formData.append("logo", '');
    }
    // demo only settable on create (backend enforces too)
    if (type_request === "post") {
        formData.append("demo", event.value.demo ? "true" : "false");
    }
    formData.append("join_lobby_enabled", event.value.join_lobby_enabled && canUseJoinLobby.value && !event.value.demo ? "true" : "false");
    formData.append("join_lobby_locations_enabled", event.value.join_lobby_locations_enabled && canUseJoinLobby.value ? "true" : "false");
    formData.append("join_lobby_locations", JSON.stringify(event.value.join_lobby_locations || []));
    formData.append("join_lobby_status", event.value.join_lobby_status || "open");

    if (selectedActivityCode.value === "CS") {
        const activityConfig = storeEvents.activityConfiguration as { team_mode?: string } | null
        if (activityConfig) {
            activityConfig.team_mode = event.value.join_lobby_enabled ? "ON_THE_FLY" : "PRECREATED"
        }
    }
    
    if('booking' in route.query && booking.value)
    {
        formData.append("booking", booking.value.id);
    }
    
    await axiosToken(type_request, "events/event/" + api_key, formData, {"Content-Type": "multipart/form-data"},)
    .then( async (response: AxiosResponse) => {
        if(response.data.api_key != undefined)
        {
            api_key = response.data.api_key
            await axiosToken('put', "events/event_full/" + api_key, storeEvents.activityConfiguration)
            .then( async (response: AxiosResponse) => {
                if(response.data.id != undefined)
                {
                    const activityConfig = storeEvents.activityConfiguration as { apply_setting_logo?: boolean }
                    if (activityConfig?.apply_setting_logo) {
                        activityConfig.apply_setting_logo = false
                    }
                    toast.add({ severity: 'success', summary: t('activities.save_ok', {message_request: message_request}), life: 3000 })
                    router.push("/events/dashboard/" + api_key)
                }
                else
                {
                    toast.add({ severity: 'error', summary: t('activities.save_ok', {message_request_negative: message_request_negative}), life: 3000 })
                }
            })
            .catch((e: any) => {
                console.log(e);
                loadingBtnSave.value = false
            });
        }
        else
        {
            toast.add({ severity: 'error', summary: t('activities.save_ok', {message_request_negative: message_request_negative}), life: 3000 })
        }
    })
    .catch((e: any) => {
        console.log(e);
        loadingBtnSave.value = false
    });
}

const validateForms = async () =>
{
    // Clear previous errors
    errors.value = {}
    
    // Validate form fields
    let isValid = true
    
    if (!event.value.name || event.value.name.trim() === '') {
        errors.value.name = t('activities.alert_name_activity')
        isValid = false
    }
    
    if (!event.value.activity) {
        errors.value.activity = t('activities.select_activity')
        isValid = false
    }
    
    if (!event.value.date) {
        errors.value.date = t('activities.select_date')
        isValid = false
    }
    
    if (!event.value.language) {
        errors.value.language = t('activities.select_language')
        isValid = false
    }
    
    if (!isValid) {
        toast.add({ severity: 'error', summary: t('general.incorrect_fields'), life: 3000 })
        return
    }
    
    console.log(event.value)
    console.log(storeEvents.activityConfiguration)
    
    // Check if validate method exists (for naive-ui forms) or skip validation
    const activityConfig = storeEvents.activityConfiguration as any
    if (activityConfig?.activity_formRef?.validate) {
        activityConfig.activity_formRef.validate((validationErrors: any) => {
            console.log(validationErrors)
            console.log("HERE")
            if (!validationErrors) {
                activityConfig.activity_formRef = null
                saveEvent()
            } else {
                console.log(validationErrors)
                toast.add({ severity: 'error', summary: t('general.incorrect_fields'), life: 3000 })
            }
        })
    } else {
        // No validation method (e.g., regular HTML form), proceed with save
        activityConfig.activity_formRef = null
        saveEvent()
    }
    console.log("HERE2")
}

const event = ref<Event>({
    id: 0, 
    name: "", 
    activity: null, 
    date: new Date(),
    challenges: [],
    language: getDefaultLanguage(),
    comments: "",
    logo: null,
    demo: false,
    join_lobby_enabled: false,
    join_lobby_locations_enabled: false,
    join_lobby_locations: [],
    join_lobby_status: "open",
}) 

type Event = {
    id: number
    name: string | null
    activity: string | null
    language: string
    date: Date | null
    challenges: Array<Object> | null
    comments: string | null
    logo: File | null
    demo: boolean
    join_lobby_enabled: boolean
    join_lobby_locations_enabled: boolean
    join_lobby_locations: string[]
    join_lobby_status: string
}

const goDashboard = () => 
{
    router.push("/events/dashboard/" + storeEvents.eventSelected.api_key);
}

const currentRef = ref(1)
const current = currentRef

watch( () => event.value.activity, () => {
    if(event.value.activity != null)
    {
        const foundActivity = (storeContent.activities as any[]).find((activity: any) => activity.id == parseInt(event.value.activity || "0"))
        if (foundActivity) {
            storeEvents.eventSelected.activity = foundActivity
        }
    }
    if (!canUseJoinLobby.value) {
        event.value.join_lobby_enabled = false
        event.value.join_lobby_locations_enabled = false
        event.value.join_lobby_locations = []
    }
})

watch(() => event.value.demo, (isDemo) => {
    if (!isDemo) return
    event.value.join_lobby_enabled = false
    event.value.join_lobby_locations_enabled = false
})

</script>

<style lang="scss" scoped>
.components-list {
	grid-template-columns: none;
}
.card-css {
    background-color: var(--bg-color);
}

:deep(.p-fileupload-buttonbar) {
    display: none;
}

:deep(.p-fileupload-content) {
    padding: 0;
}
</style>